import type { TableRowProps } from '@chakra-ui/react';

import { Box, Flex, IconButton, Menu, Table } from '@chakra-ui/react';
import { ChevronDown, ChevronUp, MoreVertical } from 'lucide-react';
import React, { useState } from 'react';

import { StatusBadge, type StatusType } from '~/components/display/StatusBadge';

// Helper function to map status string to StatusType
const mapToStatusType = (status: string): StatusType => {
  const statusLower = status.toLowerCase();

  if (statusLower === 'active') return 'active';
  if (statusLower === 'due soon') return 'due-soon';
  if (statusLower === 'past due') return 'past-due';
  return 'default';
};

export interface ColumnDefinition<T extends Record<string, unknown>> {
  key: string;
  header: string;
  accessor: keyof T | ((item: T) => React.ReactNode); // string key or render function
  isStatus?: boolean;
  headerTextAlign?: 'left' | 'center' | 'right';
  cellTextAlign?: 'left' | 'center' | 'right';
  disableSort?: boolean; // Flag to disable sorting for specific columns
}

interface ReusableTableProps<T extends Record<string, unknown>> {
  columns: ColumnDefinition<T>[];
  data: T[];
  onEdit?: (item: T) => void;
  onDelete?: (item: T) => void;
  onArchive?: (item: T) => void;
  onClone?: (item: T) => void;
  onRowClick?: (item: T) => void;
  activeRowId?: string; // New optional prop for highlighting active row
  idAccessor?: keyof T; // Optional accessor for the ID field, defaults to 'id'
  footerRow?: Partial<T> | React.ReactNode; // Optional footer row data or custom content
  footerRowStyle?: TableRowProps; // Optional styling for the footer row
}

type SortDirection = 'asc' | 'desc' | null;

export const ReusableTable = <T extends Record<string, unknown>>({
  columns,
  data,
  onEdit,
  onDelete,
  onArchive,
  onClone,
  onRowClick,
  activeRowId,
  idAccessor = 'id' as keyof T,
  footerRow,
  footerRowStyle,
}: ReusableTableProps<T>) => {
  const defaultTextColor = 'neutral.textPrimary'; // Using theme token reference

  // Check if any action handlers are provided
  const hasActions = Boolean(onEdit || onDelete || onArchive || onClone);

  // State for sorting
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  // Handle sorting
  const handleSort = (column: ColumnDefinition<T>) => {
    if (column.disableSort) return;

    // If clicking the same column, cycle through sort directions: null -> asc -> desc -> null
    if (sortColumn === column.key) {
      if (sortDirection === null) {
        setSortDirection('asc');
      } else if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        setSortDirection(null);
        setSortColumn(null);
      }
    } else {
      // New column, start with ascending sort
      setSortColumn(column.key);
      setSortDirection('asc');
    }
  };

  // Sort the data based on current sort state
  const sortedData = React.useMemo(() => {
    if (!sortColumn || !sortDirection) return data;

    return [...data].sort((a, b) => {
      const columnDef = columns.find((col) => col.key === sortColumn);
      if (!columnDef || typeof columnDef.accessor !== 'string') return 0;

      const accessor = columnDef.accessor as keyof T;
      const aValue = String(a[accessor] || '');
      const bValue = String(b[accessor] || '');

      // Check for currency values (starting with $)
      if (aValue.startsWith('$') || bValue.startsWith('$')) {
        // Remove $ and commas, convert to number
        const aNum = parseFloat(aValue.replace(/[$,]/g, '')) || 0;
        const bNum = parseFloat(bValue.replace(/[$,]/g, '')) || 0;

        return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // Check for date values (common formats)
      const dateRegex =
        /^\w{3}\s\d{1,2},?\s\d{4}$|^\d{1,2}\/\d{1,2}\/\d{4}$|^\d{4}-\d{1,2}-\d{1,2}$/;
      if (dateRegex.test(aValue) || dateRegex.test(bValue)) {
        const aDate = new Date(aValue);
        const bDate = new Date(bValue);

        // Check if dates are valid
        const aValid = !Number.isNaN(aDate.getTime());
        const bValid = !Number.isNaN(bDate.getTime());

        if (aValid && bValid) {
          return sortDirection === 'asc'
            ? aDate.getTime() - bDate.getTime()
            : bDate.getTime() - aDate.getTime();
        }
      }

      // Default: alphabetical string comparison
      if (sortDirection === 'asc') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });
  }, [data, sortColumn, sortDirection, columns]);

  return (
    <Box overflowX="auto">
      <Table.Root>
        <Table.Header>
          <Table.Row>
            {columns.map((col) => (
              <Table.ColumnHeader
                key={col.key}
                fontWeight="bold"
                color={defaultTextColor}
                cursor={col.disableSort ? 'default' : 'pointer'}
                onClick={() => handleSort(col)}
              >
                <Flex
                  alignItems="center"
                  justifyContent={
                    col.headerTextAlign === 'center'
                      ? 'center'
                      : col.headerTextAlign === 'right'
                        ? 'flex-end'
                        : 'flex-start'
                  }
                  gap={1}
                >
                  {col.header}
                  {sortColumn === col.key &&
                    (sortDirection === 'asc' ? (
                      <ChevronUp size={14} />
                    ) : sortDirection === 'desc' ? (
                      <ChevronDown size={14} />
                    ) : null)}
                </Flex>
              </Table.ColumnHeader>
            ))}
            {hasActions && (
              <Table.ColumnHeader fontWeight="bold" color={defaultTextColor} textAlign="center">
                {/* Actions column header */}
              </Table.ColumnHeader>
            )}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {sortedData.map((item, rowIndex) => {
            const isActiveRow = activeRowId && item[idAccessor] === activeRowId;

            return (
              <Table.Row
                key={String(item[idAccessor] || rowIndex)}
                cursor={onRowClick ? 'pointer' : 'default'}
                onClick={onRowClick ? () => onRowClick(item) : undefined}
                _hover={onRowClick ? { bg: 'gray.50' } : undefined}
                bg={isActiveRow ? 'blue.50' : undefined}
                borderLeft={isActiveRow ? '4px solid' : undefined}
                borderLeftColor={isActiveRow ? 'blue.500' : undefined}
                fontWeight={isActiveRow ? 'semibold' : undefined}
              >
                {columns.map((col) => {
                  let cellContent: React.ReactNode;
                  if (typeof col.accessor === 'function') {
                    cellContent = col.accessor(item);
                  } else {
                    cellContent = item[col.accessor as keyof T] as React.ReactNode;
                  }

                  if (col.isStatus) {
                    const statusString = String(
                      typeof col.accessor === 'function'
                        ? col.accessor(item)
                        : item[col.accessor as keyof T] || ''
                    );

                    const statusType = mapToStatusType(statusString);
                    cellContent = <StatusBadge status={statusType} label={statusString} />;
                  }

                  return (
                    <Table.Cell
                      key={`${col.key}-${rowIndex}`}
                      color={defaultTextColor}
                      textAlign={col.cellTextAlign || (col.isStatus ? 'center' : 'start')}
                    >
                      {cellContent}
                    </Table.Cell>
                  );
                })}
                {hasActions && (
                  <Table.Cell
                    textAlign="center"
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  >
                    <Menu.Root>
                      <Menu.Trigger asChild>
                        <IconButton aria-label="Options" variant="ghost" size="sm">
                          <MoreVertical size={16} />
                        </IconButton>
                      </Menu.Trigger>
                      <Menu.Positioner>
                        <Menu.Content>
                          {onEdit && (
                            <Menu.Item value="edit" onClick={() => onEdit(item)}>
                              Edit
                            </Menu.Item>
                          )}
                          {onClone && (
                            <Menu.Item value="clone" onClick={() => onClone(item)}>
                              Clone
                            </Menu.Item>
                          )}
                          {onDelete && (
                            <Menu.Item value="delete" onClick={() => onDelete(item)}>
                              Delete
                            </Menu.Item>
                          )}
                          {onArchive && (
                            <Menu.Item value="archive" onClick={() => onArchive(item)}>
                              Archive
                            </Menu.Item>
                          )}
                        </Menu.Content>
                      </Menu.Positioner>
                    </Menu.Root>
                  </Table.Cell>
                )}
              </Table.Row>
            );
          })}
          {footerRow && (
            <Table.Row
              {...footerRowStyle}
              fontWeight={footerRowStyle?.fontWeight || 'bold'}
              bg={footerRowStyle?.bg || 'gray.50'}
              borderTop={footerRowStyle?.borderTop || '2px solid'}
              borderTopColor={footerRowStyle?.borderTopColor || 'gray.200'}
            >
              {columns.map((col) => {
                let cellContent: React.ReactNode;
                if (
                  typeof footerRow === 'object' &&
                  footerRow !== null &&
                  !React.isValidElement(footerRow)
                ) {
                  // Handle object data for footer row
                  // For footer rows, we want to use the column accessors to maintain consistent formatting
                  // but we need to create a mock data object with the raw values for the accessors to work with

                  // Type-safe footer data creation with validation
                  const rawFooterData = { ...footerRow } as Partial<T>;

                  // Validate that we have the required property for this column
                  const hasProperty = col.key in rawFooterData;

                  if (hasProperty && typeof col.accessor === 'function') {
                    // Use the column accessor function with the raw footer data
                    cellContent = col.accessor(rawFooterData as T);
                  } else if (hasProperty) {
                    // Fallback to direct property access
                    cellContent = rawFooterData[col.key as keyof T] as React.ReactNode;
                  } else {
                    // No data available for this column
                    cellContent = '';
                  }
                } else {
                  // Handle React node footer content
                  cellContent = footerRow;
                }

                if (col.isStatus && typeof cellContent === 'string') {
                  const statusType = mapToStatusType(cellContent);
                  cellContent = <StatusBadge status={statusType} label={cellContent} />;
                }

                return (
                  <Table.Cell
                    key={`footer-${col.key}`}
                    color={defaultTextColor}
                    textAlign={col.cellTextAlign || (col.isStatus ? 'center' : 'start')}
                  >
                    {cellContent}
                  </Table.Cell>
                );
              })}
              {hasActions && (
                <Table.Cell textAlign="center">
                  {/* Empty cell for actions column in footer */}
                </Table.Cell>
              )}
            </Table.Row>
          )}
        </Table.Body>
      </Table.Root>
    </Box>
  );
};
